// Client home (oracle-prototype src/pages/AboutClient.jsx, route /client/:id): the Recap tab
// (src/components/ClientShortTermView.jsx) and the Account Profile tab (AboutConsolidatedPage,
// built from src/about/aboutData.js). Types here describe the thin fixtures; a wired seam returns
// the same shapes.

import { Design, DesignType, Intent, IntentType } from '@Components/designConstants';
import { FictionalClient } from '../../fixtures/fictional-clients';

export const ClientHomeTab = {
  RECAP: 'recap',
  PROFILE: 'profile'
} as const;
export type ClientHomeTabType = (typeof ClientHomeTab)[keyof typeof ClientHomeTab];

// The prototype names the right tab after what it holds: the account profile and the sources the
// AI reads off it (AboutClient.jsx ACCOUNT_PROFILE_TAB_LABEL).
export const CLIENT_HOME_TAB_OPTIONS = [
  { key: ClientHomeTab.RECAP, label: 'Recap' },
  { key: ClientHomeTab.PROFILE, label: 'Profile & AI Sources' }
];

export const ALL_CHANNELS = 'All channels';

// The Recap summary's date-range quick picks (AboutClient.jsx QUICK_PICKS).
export const RecapRange = {
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year'
} as const;
export type RecapRangeType = (typeof RecapRange)[keyof typeof RecapRange];

export const RECAP_RANGE_OPTIONS = [
  { key: RecapRange.WEEK, label: 'Past Week' },
  { key: RecapRange.MONTH, label: 'Month' },
  { key: RecapRange.QUARTER, label: 'Quarter' },
  { key: RecapRange.YEAR, label: 'Year' }
];

export const Importance = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
} as const;
export type ImportanceType = (typeof Importance)[keyof typeof Importance];
export const IMPORTANCE_LEVELS: ImportanceType[] = [
  Importance.HIGH,
  Importance.MEDIUM,
  Importance.LOW
];

export const SignalDirection = {
  POSITIVE: 'positive',
  RISK: 'risk'
} as const;
export type SignalDirectionType = (typeof SignalDirection)[keyof typeof SignalDirection];

// The Risk panel's filter (ClientShortTermView.jsx RISK_FILTERS): business risks are relationship
// signals, performance risks are campaign / business performance and budget & pacing.
export const RiskFilter = {
  ALL: 'all',
  BUSINESS: 'business',
  PERFORMANCE: 'performance'
} as const;
export type RiskFilterType = (typeof RiskFilter)[keyof typeof RiskFilter];
export type RiskKindType = Exclude<RiskFilterType, typeof RiskFilter.ALL>;

export const RISK_FILTER_OPTIONS = [
  { key: RiskFilter.ALL, label: 'All' },
  { key: RiskFilter.BUSINESS, label: 'Business' },
  { key: RiskFilter.PERFORMANCE, label: 'Performance' }
];

export const TaskGroup = {
  PAST_DUE: 'Past Due',
  THIS_WEEK: 'This Week',
  NEXT_WEEK: 'Next Week'
} as const;
export type TaskGroupType = (typeof TaskGroup)[keyof typeof TaskGroup];
export const TASK_GROUP_ORDER: TaskGroupType[] = [
  TaskGroup.PAST_DUE,
  TaskGroup.THIS_WEEK,
  TaskGroup.NEXT_WEEK
];

export const TrendDirection = {
  UP: 'up',
  DOWN: 'down',
  FLAT: 'flat'
} as const;
export type TrendDirectionType = (typeof TrendDirection)[keyof typeof TrendDirection];

// Badge design for a sentiment move.
export const TREND_INTENT: Record<TrendDirectionType, DesignType | IntentType> = {
  [TrendDirection.UP]: Intent.SUCCESS,
  [TrendDirection.DOWN]: Intent.ERROR,
  [TrendDirection.FLAT]: Design.NEUTRAL
};

export const SUMMARY_PROVENANCE = 'Written by the AI from your connected sources.';
export const CLIENT_HEALTH_HINT =
  "Positive and risk signals detected across this client's calls, email, and Slack, grouped by importance.";
// The prototype's Client Health pill is a display control: its events already sit in a rolling
// 90-day window, so the pill names the window rather than re-filtering.
export const CLIENT_HEALTH_WINDOW = 'Past 90 days';

export interface RecapSummary {
  // e.g. "Jul 22 – Jul 28"
  span: string;
  headline: string;
  bullets: string[];
}

export interface RecapSignal {
  id: string;
  direction: SignalDirectionType;
  // Only set on risk signals.
  riskKind?: RiskKindType;
  importance: ImportanceType;
  category: string;
  title: string;
  // M/D, the date the signal first appeared.
  dateLabel: string;
  channel: string;
}

export interface RecapTask {
  id: string;
  title: string;
  group: TaskGroupType;
  done: boolean;
  overdue: boolean;
  isNew: boolean;
  dueLabel?: string;
  channel: string;
}

export interface StakeholderSentiment {
  // Stakeholder drill-down id: `${base}/stakeholder/:id`.
  id: string;
  name: string;
  title: string;
  score: number;
  delta: number;
}

export interface RecapSentiment {
  score: number;
  delta: number;
  byPerson: StakeholderSentiment[];
}

export interface PastCall {
  id: string;
  // Touchpoint drill-down id: `${base}/touchpoint/:id`.
  touchpointId: string;
  when: string;
  title: string;
  recapNote: string;
  channel: string;
}

export interface UpcomingCall {
  id: string;
  when: string;
  title: string;
}

export interface ClientRecap {
  client: FictionalClient;
  channels: string[];
  summaries: Record<RecapRangeType, RecapSummary>;
  signals: RecapSignal[];
  tasks: RecapTask[];
  sentiment: RecapSentiment;
  pastCalls: PastCall[];
  upcomingCalls: UpcomingCall[];
}

// ---- Account Profile --------------------------------------------------------------------------

// Section source types (aboutData.js SOURCE_TYPE_META).
export const SourceType = {
  MANUAL: 'manual',
  UPLOAD_AI: 'upload+ai',
  UPLOAD_ONLY: 'upload-only',
  AI: 'ai',
  AUTO: 'auto'
} as const;
export type SourceTypeType = (typeof SourceType)[keyof typeof SourceType];

export const SOURCE_TYPE_LABELS: Record<SourceTypeType, string> = {
  [SourceType.MANUAL]: 'Manual / Upload',
  [SourceType.UPLOAD_AI]: 'Upload → AI summary',
  [SourceType.UPLOAD_ONLY]: 'Upload only',
  [SourceType.AI]: 'AI-generated',
  [SourceType.AUTO]: 'Auto'
};

export const SectionState = {
  POPULATED: 'populated',
  EMPTY: 'empty'
} as const;
export type SectionStateType = (typeof SectionState)[keyof typeof SectionState];

export interface ProfileSource {
  id: string;
  name: string;
  // e.g. "Google Doc · synced Jul 2"
  detail: string;
}

export interface ProfileField {
  label: string;
  value: string;
}

export interface ProfilePerson {
  id: string;
  name: string;
  title: string;
  role: string;
  // Set for client-side stakeholders, who have a drill-down page.
  stakeholderId?: string;
}

export interface ProfileSection {
  id: string;
  title: string;
  subtitle?: string;
  sourceType: SourceTypeType;
  required: boolean;
  state: SectionStateType;
  summary?: string;
  fields?: ProfileField[];
  people?: ProfilePerson[];
  sources: ProfileSource[];
  // Where the section's content flows next, e.g. "Recaps".
  feeds: string[];
  lastEdited?: string;
  emptyHint?: string;
}

export interface AccountProfile {
  client: FictionalClient;
  sections: ProfileSection[];
}
